// Guest page (blueprint §5). One page at /<slug>?k=<key>, switching between states.
// Nothing is ever read back from the server except the guest's count (R3); previews
// come from the files on the guest's own phone.

import { guestTheme, HEX_COLOR } from "../../worker/colors";
import { coupleNames, isAllowedPhoto, type NameOrder } from "../../worker/names";
import { TURNSTILE_ACTION } from "../../worker/turnstile";

// ---------- API shapes (§6.1) ----------

interface EventInfo {
  bride: string;
  groom: string;
  nameOrder: NameOrder;
  weddingDate: string;
  open: boolean;
  closesAt: string;
  cap: number;
  maxFileBytes: number;
  thankYou: string;
  accent: string;
  background: string | null;
  coverPhoto: string | null;
  thanksPhoto: string | null;
  turnstileSiteKey: string;
}

interface GuestInfo {
  name: string;
  used: number;
}

type ApiBody = { ok: true; [key: string]: unknown } | { ok: false; error: string; message: string };

declare global {
  interface Window {
    turnstile?: {
      render(el: HTMLElement, options: Record<string, unknown>): string;
      reset(widgetId: string): void;
    };
    onTurnstileLoad?: () => void;
  }
}

const GENERIC_THANKS = "Thank you for celebrating with us, and for sharing the moments we were too busy to see.";
const CONCURRENCY = 2;
const RETRY_DELAYS_MS = [2_000, 5_000, 15_000];
/** An upload that makes no progress for this long is aborted and retried (§5.4). */
const STALL_MS = 60_000;

// ---------- Page context ----------

const slug = decodeURIComponent(location.pathname.split("/")[1] ?? "");
const key = new URLSearchParams(location.search).get("k") ?? "";
const api = `/api/e/${encodeURIComponent(slug)}`;

let event: EventInfo;
let guest: GuestInfo | null = null;

// ---------- DOM helpers ----------

const $ = <T extends Element = HTMLElement>(selector: string, root: ParentNode = document) =>
  root.querySelector<T>(selector)!;
const $$ = <T extends Element = HTMLElement>(selector: string, root: ParentNode = document) =>
  Array.from(root.querySelectorAll<T>(selector));

/** Sets every [data-field=name] on the page. */
function fill(name: string, value: string | number) {
  for (const el of $$(`[data-field="${name}"]`)) el.textContent = String(value);
}

type State = "loading" | "unavailable" | "invalid" | "closed" | "name" | "pick" | "uploading" | "thanks" | "full";

function show(state: State) {
  for (const section of $$("[data-state]")) section.hidden = section.dataset.state !== state;
  // The invalid state shows no event details (§5.2).
  const header = $(".couple");
  header.hidden = state === "loading" || state === "unavailable" || state === "invalid";
  if (!header.hidden) showHeader(header, state);
  // Never while uploading: leaving the page would stop the uploads.
  $(".credit").hidden = state === "loading" || state === "uploading";
  window.scrollTo(0, 0);
}

/**
 * The couple's photo sits above their names, never over them, and is always shown
 * whole (D29, D33). Smaller while picking and uploading, so the button stays in
 * view. The thank-you screens swap in the thank-you photo, if there is one, so
 * there's only ever one photo. It loads only when shown, so it never competes
 * with uploads.
 */
function showHeader(header: HTMLElement, state: State) {
  const busy = state === "pick" || state === "uploading";
  const ending = state === "thanks" || state === "full";
  const thanks = ending && event.thanksPhoto;
  const photo = thanks || event.coverPhoto;
  header.dataset.photo = thanks ? "thanks" : busy ? "small" : "full";
  // Names on one line after the first screen (and whenever there's a photo, in CSS).
  header.dataset.names = busy || ending ? "line" : "full";
  header.classList.toggle("has-photo", Boolean(photo));
  const img = $<HTMLImageElement>(".cover", header);
  if (!photo) {
    img.hidden = true;
    return img.removeAttribute("src");
  }
  if (img.getAttribute("src") !== photo) {
    // Browsers keep showing the previous picture until the new one arrives, so the
    // cover would sit on the thank-you screen on a slow line. Hide it until loaded.
    img.hidden = true;
    img.onload = () => {
      if (img.getAttribute("src") === photo) img.hidden = false;
    };
    img.src = photo;
  } else if (img.complete && img.naturalWidth) {
    img.hidden = false;
  }
}

/** --cap, --used and --picked drive the dot row (styles.css). */
function setSlots(used: number, picked: number) {
  for (const row of $$(".slots")) {
    row.style.setProperty("--cap", String(event.cap));
    row.style.setProperty("--used", String(used));
    row.style.setProperty("--picked", String(picked));
  }
}

function plural(n: number, word: string) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/** "2026-12-12" → "Saturday, 12 December 2026". The date is a calendar date, so format it in UTC. */
function formatDate(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const weekday = date.toLocaleDateString("en-GB", { weekday: "long", timeZone: "UTC" });
  const rest = date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
  return `${weekday}, ${rest}`;
}

/** Text on the accent colour: white on dark accents, ink on light ones. Light accents are
 *  also too faint as text on the page, so --accent-ink darkens them. */
const prefersDark = matchMedia("(prefers-color-scheme: dark)");

/** The event's accent and optional background (D27). With no background the page
 *  keeps following the phone's light or dark setting, so it listens for changes. */
function applyTheme(accent: string, background: string | null) {
  if (!HEX_COLOR.test(accent)) return;
  const bg = background && HEX_COLOR.test(background) ? background : null;
  const { dark, vars } = guestTheme(accent, bg, prefersDark.matches);
  for (const [name, value] of Object.entries(vars)) document.body.style.setProperty(name, value);
  document.documentElement.style.colorScheme = dark ? "dark" : "light";
  prefersDark.onchange = bg ? null : () => applyTheme(accent, null);
}

async function request(method: string, path: string, body?: unknown): Promise<{ status: number; body: ApiBody }> {
  const response = await fetch(path, {
    method,
    headers: body === undefined ? { "X-Event-Key": key } : { "X-Event-Key": key, "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const parsed = (await response.json().catch(() => null)) as ApiBody | null;
  return {
    status: response.status,
    body: parsed ?? { ok: false, error: "bad_response", message: "Something went wrong. Please try again." },
  };
}

// ---------- Load ----------

async function load() {
  show("loading");
  if (!slug || !key) return show("invalid");

  let result;
  try {
    result = await request("GET", `${api}?k=${encodeURIComponent(key)}`);
  } catch {
    return show("unavailable");
  }
  if (result.status === 404) return show("invalid");
  if (!result.body.ok) return show("unavailable");

  event = result.body.event as EventInfo;
  guest = result.body.guest as GuestInfo | null;

  applyTheme(event.accent, event.background);
  const [first, second] = coupleNames(event.bride, event.groom, event.nameOrder);
  document.title = `${first} & ${second} · Share your photos`;
  fill("first", first);
  fill("second", second);
  fill("wedding-date", formatDate(event.weddingDate));
  fill("cap", event.cap);
  fill("thank-you", event.thankYou.trim() || GENERIC_THANKS);

  if (!event.open) return show("closed");
  if (!guest) return showName();
  showPick("Welcome back,");
}

$('[data-action="reload"]').addEventListener("click", () => void load());

// ---------- Name (§5.2) + Turnstile ----------

const nameForm = $<HTMLFormElement>('[data-form="name"]');
const nameInput = $<HTMLInputElement>("#guest-name");
const nameButton = $<HTMLButtonElement>('button[type="submit"]', nameForm);

let turnstileWidget: string | null = null;
let turnstileToken: string | null = null;
let turnstileWaiters: ((token: string | null) => void)[] = [];
/** True while Turnstile shows its checkbox and waits for the guest to tick it. */
let turnstileInteractive = false;

function settleTurnstile(token: string | null) {
  turnstileToken = token;
  for (const resolve of turnstileWaiters) resolve(token);
  turnstileWaiters = [];
}

function renderTurnstile() {
  const render = () => {
    if (turnstileWidget !== null || !window.turnstile) return;
    turnstileWidget = window.turnstile.render($("[data-turnstile]"), {
      sitekey: event.turnstileSiteKey,
      action: TURNSTILE_ACTION,
      // Invisible unless Cloudflare needs the guest to tick a box.
      appearance: "interaction-only",
      callback: (token: string) => settleTurnstile(token),
      "expired-callback": () => (turnstileToken = null),
      "error-callback": () => settleTurnstile(null),
      "before-interactive-callback": () => {
        turnstileInteractive = true;
        if (nameButton.disabled) nameButton.textContent = "Tick the box above to continue";
      },
      "after-interactive-callback": () => (turnstileInteractive = false),
    });
  };
  if (window.turnstile) return render();
  if (document.querySelector("script[data-turnstile-api]")) return;
  window.onTurnstileLoad = render;
  const script = document.createElement("script");
  script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad";
  script.async = true;
  script.dataset.turnstileApi = "";
  script.onerror = () => settleTurnstile(null);
  document.head.append(script);
}

/** The current token, waiting up to 20 s for the invisible check to finish. If Turnstile
 *  asks the guest to tick its box, keep waiting: the submit carries on once they do. */
function getTurnstileToken(): Promise<string | null> {
  if (turnstileToken) return Promise.resolve(turnstileToken);
  if (turnstileInteractive) nameButton.textContent = "Tick the box above to continue";
  return new Promise((resolve) => {
    turnstileWaiters.push(resolve);
    const giveUp = () => (turnstileInteractive ? setTimeout(giveUp, 20_000) : resolve(null));
    setTimeout(giveUp, 20_000);
  });
}

function showName() {
  show("name");
  fill("name-error", "");
  renderTurnstile();
}

nameForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  const name = nameInput.value.replace(/\s+/g, " ").trim();
  if (!name) {
    fill("name-error", "Please enter your name.");
    return nameInput.focus();
  }

  fill("name-error", "");
  nameButton.disabled = true;
  nameButton.textContent = "Just a moment…";
  try {
    const token = await getTurnstileToken();
    if (!token) {
      fill("name-error", "We couldn't confirm you're a person. Please reload the page and try again.");
      return;
    }
    const result = await request("POST", `${api}/guest`, { name, turnstileToken: token });
    // A token can only be used once.
    turnstileToken = null;
    if (turnstileWidget !== null) window.turnstile?.reset(turnstileWidget);

    if (result.body.ok) {
      guest = result.body.guest as GuestInfo;
      return showPick("Hi");
    }
    if (result.status === 404) return show("invalid");
    if (result.body.error === "closed") return show("closed");
    fill("name-error", result.body.message);
  } catch {
    fill("name-error", "We couldn't reach the server. Check your connection and try again.");
  } finally {
    nameButton.disabled = false;
    nameButton.textContent = "Continue";
  }
});

// ---------- Photo items ----------

type Status = "waiting" | "uploading" | "done" | "failed" | "rejected";

interface Item {
  file: File;
  el: HTMLLIElement;
  url: string;
  status: Status;
  progress: number;
  message: string;
}

const tileTemplate = $<HTMLTemplateElement>("#photo-tile");
const pickedList = $('[data-list="picked"]');
const queueList = $('[data-list="queue"]');

let picked: Item[] = [];
let queue: Item[] = [];

function makeItem(file: File): Item {
  const el = tileTemplate.content.firstElementChild!.cloneNode(true) as HTMLLIElement;
  const url = URL.createObjectURL(file);
  const img = $<HTMLImageElement>("img", el);
  // HEIC outside Safari, DNG: the browser can't draw it, so show a plain tile with the name.
  img.addEventListener("error", () => {
    img.remove();
    el.classList.add("no-preview");
  });
  img.src = url;
  $(".tile-name", el).textContent = file.name;
  const item: Item = { file, el, url, status: "waiting", progress: 0, message: "" };
  $(".tile-action", el).addEventListener("click", () => onTileAction(item));
  return item;
}

function dropItem(item: Item) {
  URL.revokeObjectURL(item.url);
  item.el.remove();
}

/** Brings a tile's markup in line with its status and the list it's in. */
function renderTile(item: Item) {
  const inQueue = item.el.parentElement === queueList;
  const status = $(".tile-status", item.el);
  const action = $<HTMLButtonElement>(".tile-action", item.el);
  const bar = $<HTMLProgressElement>("progress", item.el);
  item.el.dataset.status = item.status;
  action.hidden = true;
  status.textContent = "";

  switch (item.status) {
    case "waiting":
      if (inQueue) status.textContent = "Waiting";
      else showAction(action, "Remove", `Remove ${item.file.name}`);
      break;
    case "uploading":
      bar.value = item.progress;
      status.textContent = `${item.progress}%`;
      break;
    case "done":
      status.textContent = "Done";
      break;
    case "failed":
      showAction(action, "Failed. Tap to retry", `Retry ${item.file.name}`);
      break;
    case "rejected":
      status.textContent = item.message;
      if (!inQueue) showAction(action, "Remove", `Remove ${item.file.name}`);
      break;
  }
}

function showAction(button: HTMLButtonElement, label: string, ariaLabel: string) {
  button.hidden = false;
  button.textContent = label;
  button.setAttribute("aria-label", ariaLabel);
}

function onTileAction(item: Item) {
  if (item.status === "failed") {
    item.status = "waiting";
    renderTile(item);
    startQueue();
    return;
  }
  // Remove, from the picked list.
  picked = picked.filter((p) => p !== item);
  dropItem(item);
  fill("pick-notice", "");
  renderPick();
}

// ---------- Pick (§5.3) ----------

const photoInput = $<HTMLInputElement>("#photo-input");
const uploadButton = $<HTMLButtonElement>('[data-action="upload"]');

const remaining = () => Math.max(0, event.cap - (guest?.used ?? 0));
const accepted = () => picked.filter((p) => p.status === "waiting");

function showPick(greeting: string) {
  if (!guest) return showName();
  if (remaining() === 0) return showFull();
  fill("greeting", greeting);
  fill("guest-name", guest.name);
  fill("pick-notice", "");
  show("pick");
  renderPick();
}

function renderPick() {
  const used = guest?.used ?? 0;
  const count = accepted().length;
  fill("used", used);
  fill("remaining", remaining() - count);
  $(".picker").hidden = remaining() - count === 0;
  setSlots(used, count);
  uploadButton.hidden = count === 0;
  uploadButton.textContent = `Upload ${plural(count, "photo")}`;
}

photoInput.addEventListener("change", () => {
  const files = Array.from(photoInput.files ?? []);
  // Clear it so picking the same photo again still fires "change".
  photoInput.value = "";
  if (!files.length) return;

  const maxMb = Math.floor(event.maxFileBytes / 1_000_000);
  let free = remaining() - accepted().length;
  let added = 0;
  let dropped = 0;

  for (const file of files) {
    let rejection = "";
    if (!isAllowedPhoto(file.name, file.type)) rejection = "Not a photo";
    else if (file.size > event.maxFileBytes) rejection = `Too large (max ${maxMb} MB)`;
    else if (free <= 0) {
      dropped++;
      continue;
    } else {
      free--;
      added++;
    }

    const item = makeItem(file);
    if (rejection) {
      item.status = "rejected";
      item.message = rejection;
    }
    picked.push(item);
    pickedList.append(item.el);
    renderTile(item);
  }

  fill("pick-notice", dropped ? `We added the first ${added}. You can share up to ${event.cap} in total.` : "");
  renderPick();
});

uploadButton.addEventListener("click", () => {
  const toSend = accepted();
  if (!toSend.length) return;
  // Rejected tiles are never sent; a new round starts with a fresh queue.
  for (const item of picked) if (item.status !== "waiting") dropItem(item);
  clearQueue();
  picked = [];
  queue = toSend;
  for (const item of queue) {
    queueList.append(item.el);
    renderTile(item);
  }
  show("uploading");
  startQueue();
});

// ---------- Upload queue (§5.4) ----------

/** Items held by an upload worker, including ones waiting out a retry delay. */
const inFlight = new Set<Item>();
/** Bumped by halt(); workers from an earlier round stop touching the page. */
let round = 0;
/** Requests on the wire, so halt() can stop them. */
const activeXhrs = new Set<XMLHttpRequest>();
let wakeLock: WakeLockSentinel | null = null;

function clearQueue() {
  for (const item of queue) dropItem(item);
  queue = [];
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function contentTypeFor(file: File): string {
  if (file.type) return file.type;
  // Some browsers send HEIC and DNG with no type; R2 should still store a sensible one.
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const byExt: Record<string, string> = {
    jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", heic: "image/heic", heif: "image/heif",
    webp: "image/webp", avif: "image/avif", dng: "image/x-adobe-dng",
  };
  return byExt[ext] ?? "application/octet-stream";
}

/** One PUT with the raw file as the body. XHR because fetch can't report upload progress.
 *  No fixed timeout (a 75 MB DNG on a slow line is fine); a stall watchdog instead. */
function send(item: Item): Promise<{ status: number; body: ApiBody | null }> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    let lastProgress = Date.now();
    const watchdog = setInterval(() => {
      // Abort resolves with status 0, which the retry path treats as a network error.
      if (Date.now() - lastProgress > STALL_MS) xhr.abort();
    }, 5_000);
    const settle = (result: { status: number; body: ApiBody | null }) => {
      clearInterval(watchdog);
      activeXhrs.delete(xhr);
      resolve(result);
    };
    activeXhrs.add(xhr);
    xhr.open("PUT", `${api}/photos`);
    xhr.setRequestHeader("Content-Type", contentTypeFor(item.file));
    xhr.setRequestHeader("X-Event-Key", key);
    xhr.setRequestHeader("X-File-Name", encodeURIComponent(item.file.name));
    // The server's reply after the last byte also gets STALL_MS.
    xhr.upload.onload = () => (lastProgress = Date.now());
    xhr.upload.onprogress = (e) => {
      lastProgress = Date.now();
      if (!e.lengthComputable) return;
      // Hold at 99% until the server confirms the photo is stored.
      item.progress = Math.min(99, Math.floor((e.loaded / e.total) * 100));
      renderTile(item);
      renderQueue();
    };
    xhr.onload = () => {
      let body: ApiBody | null = null;
      try {
        body = JSON.parse(xhr.responseText) as ApiBody;
      } catch {}
      settle({ status: xhr.status, body });
    };
    xhr.onerror = xhr.onabort = () => settle({ status: 0, body: null });
    xhr.send(item.file);
  });
}

const retryable = (status: number) => status === 0 || status === 429 || status >= 500;

async function upload(item: Item, myRound: number) {
  for (let attempt = 0; ; attempt++) {
    item.status = "uploading";
    item.progress = 0;
    renderTile(item);
    renderQueue();

    const { status, body } = await send(item);
    if (myRound !== round) return;

    if (body?.ok) {
      item.status = "done";
      if (guest) guest.used = Number(body.used);
      return;
    }
    if (status === 401) {
      // The album is gone (cookies cleared mid-session): ask for the name again.
      guest = null;
      return halt(showName);
    }
    if (status === 404) return halt(() => show("invalid"));
    if (body?.error === "closed") return halt(() => show("closed"));

    if (!retryable(status)) {
      // The server's message is written for guests (§6).
      item.status = "rejected";
      item.message = body?.message ?? "This photo couldn't be uploaded.";
      if (body?.error === "cap_reached" && guest) guest.used = event.cap;
      return;
    }
    if (attempt >= RETRY_DELAYS_MS.length) {
      item.status = "failed";
      return;
    }
    item.status = "waiting";
    renderTile(item);
    await sleep(RETRY_DELAYS_MS[attempt]);
    if (myRound !== round) return;
  }
}

/** Stops the queue and switches screens. Photos that finished are already stored and counted. */
function halt(next: () => void) {
  round++;
  inFlight.clear();
  // Stop the other upload too, so nothing lands after the page has moved on.
  for (const xhr of activeXhrs) xhr.abort();
  releaseWakeLock();
  next();
}

/** Shows the uploading screen and runs every waiting item. */
function startQueue() {
  if (inFlight.size === 0) {
    fill("uploading-title", "Uploading");
    $(".keep-open").hidden = false;
    $('[data-state="uploading"] [data-action="retry-failed"]').hidden = true;
    show("uploading");
    void holdWakeLock();
  }
  pump();
}

/** Keeps up to CONCURRENCY uploads running; calls finish() when the last one settles. */
function pump() {
  const myRound = round;
  while (inFlight.size < CONCURRENCY) {
    const next = queue.find((item) => item.status === "waiting" && !inFlight.has(item));
    if (!next) break;
    inFlight.add(next);
    void upload(next, myRound).finally(() => {
      if (myRound !== round) return;
      inFlight.delete(next);
      renderTile(next);
      pump();
      if (inFlight.size === 0) finish();
    });
  }
  renderQueue();
}

function renderQueue() {
  const total = queue.length;
  const done = queue.filter((item) => item.status === "done").length;
  const partial = queue.reduce((sum, item) => sum + (item.status === "uploading" ? item.progress / 100 : 0), 0);
  fill("done-count", done);
  fill("total-count", total);
  const bar = $(".overall");
  bar.setAttribute("aria-valuemax", String(total));
  bar.setAttribute("aria-valuenow", String(done));
  $("span", bar).style.width = `${total ? ((done + partial) / total) * 100 : 0}%`;
}

function finish() {
  releaseWakeLock();
  const done = queue.filter((item) => item.status === "done").length;
  const failed = queue.filter((item) => item.status === "failed").length;

  if (done === 0) {
    // Nothing got through: stay here so the guest can see each tile and retry.
    fill("uploading-title", "Your photos didn't upload");
    $(".keep-open").hidden = true;
    $('[data-state="uploading"] [data-action="retry-failed"]').hidden = failed === 0;
    return;
  }
  if (remaining() === 0) return showFull();

  const used = guest?.used ?? 0;
  fill("used", used);
  setSlots(used, 0);
  fill("failed-notice", failed ? `${plural(failed, "photo")} didn't upload.` : "");
  $('[data-state="thanks"] [data-action="retry-failed"]').hidden = failed === 0;
  $('[data-action="add-more"]').hidden = false;
  show("thanks");
}

function showFull() {
  setSlots(event.cap, 0);
  show("full");
}

for (const button of $$('[data-action="retry-failed"]')) {
  button.addEventListener("click", () => {
    for (const item of queue) {
      if (item.status !== "failed") continue;
      item.status = "waiting";
      renderTile(item);
    }
    startQueue();
  });
}

$('[data-action="add-more"]').addEventListener("click", () => {
  clearQueue();
  showPick("Hi");
});

// ---------- Screen wake lock ----------
// Phones suspend uploads when the screen locks, so keep it on while the queue runs.

async function holdWakeLock() {
  if (wakeLock || !("wakeLock" in navigator)) return;
  try {
    wakeLock = await navigator.wakeLock.request("screen");
    wakeLock.addEventListener("release", () => (wakeLock = null));
  } catch {
    // Not allowed (low battery, background tab): uploads still run while the screen is on.
  }
}

function releaseWakeLock() {
  void wakeLock?.release();
  wakeLock = null;
}

// The browser drops the lock when the page is hidden; take it back on return.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && inFlight.size > 0) void holdWakeLock();
});

window.addEventListener("beforeunload", (e) => {
  if (inFlight.size > 0) e.preventDefault();
});

void load();
